/* eslint-disable react/jsx-props-no-spreading */
import * as z from 'zod';

import {
  Button,
  ButtonGroup,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';

import SolidButton from '@/client/components/shared/buttons/SolidButton';
import { useAuthStore } from '@/client/services/state/authStore';
import { useMutation } from '@tanstack/react-query';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';
import v3ApiService from '@/client/services/api/clients/v3ApiClient';
import { zodResolver } from '@hookform/resolvers/zod';

interface PasswordResetFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PasswordResetFormModal({ isOpen, onClose }: PasswordResetFormModalProps) {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const { _id: userId } = user;

  const passwordSchema = z
    .object({
      newPassword: z
        .string()
        .min(1, { message: `${t('learners.profile.settings.password.newRequired')}` }),
      confirmPassword: z
        .string()
        .min(1, { message: `${t('learners.profile.settings.password.confirmRequired')}` }),
    })
    .superRefine((data, ctx) => {
      if (data.newPassword !== data.confirmPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${t('learners.profile.settings.password.matchError')}`,
          path: ['confirmPassword'],
        });
      }
    });

  const methods = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, isValid, errors },
  } = methods;

  const handleClose = () => {
    reset();
    onClose();
  };

  const updateUserPassword = useMutation({
    mutationFn: (formData: { newPassword: string; confirmPassword: string }) =>
      v3ApiService.updateUserPassword(userId, formData.newPassword),
    onSuccess: () => {
      setToast({
        show: true,
        status: 'success',
        title: t('modules.notifications.password.updated'),
      });
      reset();
      handleClose();
    },
    onError: () =>
      setToast({
        show: true,
        status: 'error',
        title: t('modules.notifications.canNotUpdatePassword'),
      }),
  });

  const { status: mutationStatus } = updateUserPassword;

  const onSubmit = (formData: { newPassword: string; confirmPassword: string }) => {
    updateUserPassword.mutateAsync(formData);
  };

  return (
    <Modal isCentered isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalContent padding={4}>
            <ModalHeader>
              <Text variant="modalHeader">
                {t('learners.profile.settings.password.changePassword')}
              </Text>
            </ModalHeader>

            <ModalBody>
              <Stack spacing={4} paddingY={4}>
                <Stack spacing={2}>
                  <Text variant="formlabel">
                    {t('learners.profile.settings.password.newPassword')}
                  </Text>

                  <Input
                    type="password"
                    placeholder="Type new password"
                    {...register('newPassword')}
                    variant="warmNeutral"
                    isInvalid={!!errors.newPassword}
                  />
                  {errors.newPassword && (
                    <Text variant="formErrorText">{errors.newPassword.message}</Text>
                  )}
                </Stack>

                <Stack spacing={2}>
                  <Text variant="formlabel">
                    {t('learners.profile.settings.password.confirmNewPassword')}
                  </Text>

                  <Input
                    type="password"
                    placeholder={`${t('learners.profile.settings.password.confirmNewPassword')}`}
                    {...register('confirmPassword')}
                    variant="warmNeutral"
                    isInvalid={!!errors.confirmPassword}
                  />
                  {errors.confirmPassword && (
                    <Text variant="formErrorText">{errors.confirmPassword.message}</Text>
                  )}
                </Stack>
              </Stack>
            </ModalBody>

            <ModalFooter>
              <ButtonGroup spacing={4}>
                <Button variant="cancel" onClick={handleClose}>
                  {t('learners.profile.settings.password.cancel')}
                </Button>

                <SolidButton
                  type="submit"
                  isDisabled={!isDirty || !isValid}
                  isLoading={mutationStatus === 'loading'}
                >
                  {t('learners.profile.settings.password.updatePassword')}
                </SolidButton>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </form>
      </FormProvider>
    </Modal>
  );
}
